import { useHttp } from "../http/useHttp";
import {
  DATE_FORMAT_REPORT_FILENAME_DEFAULT,
  dayjs,
  makeFilename,
  useDefaultError,
} from "@synota-io/synota-shared-ui";
import { useAxiosMutation, UseAxiosMutationOptions } from "../http/useAxiosMutation";
import { paths } from "../generated-types";

type ServiceChargeInvoicesReportOperation =
  paths["/dashboard/users/{api_token_uuid}/service-charges/invoices/reports/{format}"]["post"];
type ServiceChargeInvoicesReportRequestBody =
  ServiceChargeInvoicesReportOperation["requestBody"]["content"]["application/json"];
type ServiceChargeInvoicesReportPath = ServiceChargeInvoicesReportOperation["parameters"]["path"];

export type UseServiceChargeInvoicesReportInput = ServiceChargeInvoicesReportRequestBody & {
  user_uuid: ServiceChargeInvoicesReportPath["api_token_uuid"];
};

export const useServiceChargeInvoicesReport = (
  input: UseServiceChargeInvoicesReportInput,
  options: Omit<
    UseAxiosMutationOptions<Blob, UseServiceChargeInvoicesReportInput>,
    "mutationKey" | "mutationFn"
  > = {},
) => {
  const { client } = useHttp();

  const { status, isPending, error, mutate } = useAxiosMutation<
    Blob,
    UseServiceChargeInvoicesReportInput
  >({
    mutationKey: ["service-charge-invoices-report-csv", input],
    mutationFn: (input) => {
      return client.post(`/users/${input.user_uuid}/service-charges/invoices/reports/csv`, input, {
        responseType: "blob",
      });
    },
    ...options,
  });

  useDefaultError(error, "Failed to download payment report, please try again");

  const download = () => {
    mutate(input, {
      onSuccess(data) {
        const blob = data.data;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          makeFilename(
            "Service Charge Details",
            dayjs(input.energy_date_start).format(DATE_FORMAT_REPORT_FILENAME_DEFAULT),
            input.energy_date_end
              ? dayjs(input.energy_date_end).format(DATE_FORMAT_REPORT_FILENAME_DEFAULT)
              : undefined,
          )("csv"),
        );
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
      },
    });
  };

  return {
    download,
    error,
    isPending,
    status,
  };
};

export type UseServiceChargeInvoicesReportReturn = ReturnType<
  typeof useServiceChargeInvoicesReport
>;
